import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { createArea, deleteArea, updateArea } from "../../api/area";
import customFetch from "hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";

const AreaTab = () => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [newArea, setNewArea] = useState({ code: "", eid: "" });
  const [emirates, setEmirates] = useState([]);

  useEffect(() => {
    fetchAreas();
    fetchEmirates();
  }, []);

  const fetchAreas = async () => {
    try {
      const data = await customFetch("areas");
      if (data.status && Array.isArray(data.data)) {
        setRows(data.data);
      } else {
        console.error("Unexpected response format:", data);
      }
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  const fetchEmirates = async () => {
    try {
      const data = await customFetch("emirates");
      setEmirates(data);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setNewArea({ code: "", eid: "" });
  };

  const handleSubmitNewArea = async () => {
    if (newArea.code && newArea.eid) {
      try {
        const response = await createArea(newArea);
        if (response.status && response.data) {
          const newAreaData = Array.isArray(response.data) ? response.data[0] : response.data;
          const formattedNewArea = {
            ...newAreaData,
            emirateName: emirates.find((emirate) => emirate.eid === newAreaData.eid)?.name,
          };
          setRows((prevRows) => [...prevRows, formattedNewArea]);
          handleCloseModal();
        } else {
          console.error("Unexpected response format:", response);
        }
      } catch (error) {
        console.error("Error creating area:", error);
      }
    }
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setSelectedRowId(id);
    setOpenConfirmDialog(true);
  };

  const handleDialogClose = (confirmed) => {
    setOpenConfirmDialog(false);
    if (confirmed) {
      handleConfirmDelete();
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteArea(selectedRowId);
      setRows(rows.filter((row) => row.aid !== selectedRowId));
    } catch (error) {
      console.error("Error deleting area:", error);
    }
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    updateArea(newRow.aid, updatedRow).then(() => {
      setRows(rows.map((row) => (row.aid === newRow.aid ? updatedRow : row)));
    });
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    // { field: 'aid', headerName: 'AID', width: 100 },
    { field: "code", headerName: "Code", width: 120, editable: true },
    { field: "emirateName", headerName: "Emirate", flex: 1, minWidth: 150 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 130,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={
                <Button variant="text" sx={{ minWidth: 20, padding: "0 0px" }}>
                  Save
                </Button>
              }
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={
                <Button variant="text" sx={{ minWidth: 40, padding: "0 0px" }}>
                  Cancel
                </Button>
              }
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ height: 500, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={handleOpenModal}>
          Add New Area
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        getRowId={(row) => row.aid}
      />
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-area-modal"
        aria-describedby="modal-to-add-new-area"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <h2 id="add-area-modal">Add New Area</h2>
          <TextField
            autoFocus
            margin="dense"
            id="code"
            label="Area Code"
            type="text"
            fullWidth
            variant="standard"
            value={newArea.code}
            onChange={(e) => setNewArea({ ...newArea, code: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="emirate-select-label">Emirate</InputLabel>
            <Select
              labelId="emirate-select-label"
              id="emirate-select"
              value={newArea.eid}
              label="Emirate"
              onChange={(e) => setNewArea({ ...newArea, eid: e.target.value })}
            >
              {emirates?.map((emirate) => (
                <MenuItem key={emirate.eid} value={emirate.eid}>
                  {emirate.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleCloseModal} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitNewArea} variant="contained">
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this area? This action cannot be undone."
        onClose={handleDialogClose}
        onConfirm={() => handleDialogClose(true)}
      />
    </Box>
  );
};

export default AreaTab;
